import { useState, useRef, useEffect } from 'react';
import { X, Heart, Check, Info, ChevronUp, ExternalLink, DollarSign, Users, Music } from 'lucide-react';
import { getMatchPercentage, getMatchLabel } from '../lib/matching';
import type { Band, SwipeAction } from '../types/database';
import styles from './BandCard.module.css';

interface BandCardProps {
  band: Band;
  score: number;
  reasons: string[];
  breakdown: {
    budget: number;
    vibe: number;
    music: number;
    crowd: number;
    friends: number;
  };
  onAction: (action: SwipeAction, timeSpent: number, expanded: boolean) => void;
  showConnect?: boolean; // Only show Connect button for already-matched bands
}

export function BandCard({
  band,
  score,
  reasons,
  breakdown,
  onAction,
  showConnect = false,
}: BandCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [dragX, setDragX] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef(Date.now());
  const startXRef = useRef(0);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    startTimeRef.current = Date.now();
    setExpanded(false);
  }, [band.id]);

  const getTimeSpent = () => Math.floor((Date.now() - startTimeRef.current) / 1000);

  const handleDragStart = (clientX: number) => {
    isDraggingRef.current = true;
    startXRef.current = clientX;
  };

  const handleDragMove = (clientX: number) => {
    if (!isDraggingRef.current) return;
    const diff = clientX - startXRef.current;
    setDragX(diff);

    if (diff > 50) {
      setSwipeDirection('right');
    } else if (diff < -50) {
      setSwipeDirection('left');
    } else {
      setSwipeDirection(null);
    }
  };

  const handleDragEnd = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;

    if (Math.abs(dragX) > 100) {
      // Swipe right = match, swipe left = pass
      const action: SwipeAction = dragX > 0 ? 'match' : 'pass';
      onAction(action, getTimeSpent(), expanded);
    }

    setDragX(0);
    setSwipeDirection(null);
  };

  const handleMouseDown = (e: React.MouseEvent) => handleDragStart(e.clientX);
  const handleMouseMove = (e: React.MouseEvent) => handleDragMove(e.clientX);
  const handleMouseUp = () => handleDragEnd();
  const handleMouseLeave = () => handleDragEnd();

  const handleTouchStart = (e: React.TouchEvent) => handleDragStart(e.touches[0].clientX);
  const handleTouchMove = (e: React.TouchEvent) => handleDragMove(e.touches[0].clientX);
  const handleTouchEnd = () => handleDragEnd();

  const handlePass = () => onAction('pass', getTimeSpent(), expanded);
  const handleMatch = () => onAction('match', getTimeSpent(), expanded);
  const handleConnect = () => onAction('connect', getTimeSpent(), expanded);

  const formatPrice = (min?: number | null, max?: number | null) => {
    if (!min && !max) return 'Price TBA';
    if (min === max) return `$${min}`;
    return `$${min} - $${max}`;
  };

  const vibeLabels: Record<string, string> = {
    bacchanal: 'Bacchanal',
    pretty: 'Pretty Mas',
    music: 'Music',
    social: 'Social',
    cultural: 'Cultural',
  };

  const musicLabels: Record<string, string> = {
    soca: 'Soca',
    dancehall: 'Dancehall',
    afrobeats: 'Afrobeats',
    bouyon: 'Bouyon',
    everything: 'Everything',
  };

  return (
    <div
      ref={cardRef}
      className={`${styles.card} ${expanded ? styles.expanded : ''} ${swipeDirection ? styles[swipeDirection] : ''}`}
      style={{ transform: `translateX(${dragX}px) rotate(${dragX * 0.02}deg)` }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Swipe indicators */}
      <div className={`${styles.indicator} ${styles.pass} ${swipeDirection === 'left' ? styles.visible : ''}`}>
        <X size={32} />
        <span>PASS</span>
      </div>
      <div className={`${styles.indicator} ${styles.match} ${swipeDirection === 'right' ? styles.visible : ''}`}>
        <Heart size={32} />
        <span>MATCH</span>
      </div>

      {/* Hero image */}
      <div className={styles.imageContainer}>
        {band.hero_image_url ? (
          <img src={band.hero_image_url} alt={band.name} className={styles.image} />
        ) : (
          <div className={styles.placeholder}>
            <span>{band.name[0]}</span>
          </div>
        )}
        <div className={styles.gradient} />

        {/* Match score badge */}
        <div className={styles.scoreBadge}>
          <span className={styles.scoreValue}>{getMatchPercentage(score)}</span>
          <span className={styles.scoreLabel}>{getMatchLabel(score)}</span>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.name}>{band.name}</h2>
            <div className={styles.meta}>
              <span className={styles.metaItem}>
                <DollarSign size={14} />
                {formatPrice(band.price_min, band.price_max)}
              </span>
              {band.section_count && (
                <span className={styles.metaItem}>
                  <Users size={14} />
                  {band.section_count} sections
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Vibe & Music tags */}
        <div className={styles.tags}>
          {band.vibe_tags?.map((tag) => (
            <span key={tag} className={styles.tag}>
              {vibeLabels[tag] || tag}
            </span>
          ))}
          {band.music_tags?.map((tag) => (
            <span key={tag} className={`${styles.tag} ${styles.musicTag}`}>
              <Music size={12} />
              {musicLabels[tag] || tag}
            </span>
          ))}
        </div>

        {/* Match reasons */}
        {reasons.length > 0 && (
          <div className={styles.reasons}>
            {reasons.slice(0, 2).map((reason, i) => (
              <p key={i} className={styles.reason}>
                <Check size={14} />
                {reason}
              </p>
            ))}
          </div>
        )}

        {/* Expand/collapse button */}
        <button
          className={styles.expandBtn}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? (
            <>
              <ChevronUp size={16} />
              Show less
            </>
          ) : (
            <>
              <Info size={16} />
              More info
            </>
          )}
        </button>

        {/* Expanded content */}
        {expanded && (
          <div className={styles.expandedContent}>
            {band.description && (
              <p className={styles.description}>{band.description}</p>
            )}

            {/* Score breakdown */}
            <div className={styles.breakdown}>
              <h4>Match Breakdown</h4>
              <div className={styles.breakdownItem}>
                <span>Budget fit</span>
                <div className={styles.breakdownBar}>
                  <div style={{ width: `${breakdown.budget}%` }} />
                </div>
                <span className={styles.breakdownPercent}>{breakdown.budget}%</span>
              </div>
              <div className={styles.breakdownItem}>
                <span>Vibe match</span>
                <div className={styles.breakdownBar}>
                  <div style={{ width: `${breakdown.vibe}%` }} />
                </div>
                <span className={styles.breakdownPercent}>{breakdown.vibe}%</span>
              </div>
              <div className={styles.breakdownItem}>
                <span>Music match</span>
                <div className={styles.breakdownBar}>
                  <div style={{ width: `${breakdown.music}%` }} />
                </div>
                <span className={styles.breakdownPercent}>{breakdown.music}%</span>
              </div>
              <div className={styles.breakdownItem}>
                <span>Crowd match</span>
                <div className={styles.breakdownBar}>
                  <div style={{ width: `${breakdown.crowd}%` }} />
                </div>
                <span className={styles.breakdownPercent}>{breakdown.crowd}%</span>
              </div>
              {breakdown.friends > 0 && (
                <div className={styles.breakdownItem}>
                  <span>Friends</span>
                  <div className={styles.breakdownBar}>
                    <div style={{ width: `${breakdown.friends}%` }} />
                  </div>
                  <span className={styles.breakdownPercent}>{breakdown.friends}%</span>
                </div>
              )}
            </div>

            {/* Inclusives */}
            {band.inclusives && band.inclusives.length > 0 && (
              <div className={styles.inclusives}>
                <h4>What's Included</h4>
                <div className={styles.inclusivesList}>
                  {band.inclusives.map((item, i) => (
                    <span key={i} className={styles.inclusiveItem}>
                      <Check size={12} />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Links */}
            <div className={styles.links}>
              {band.website_url && (
                <a
                  href={band.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={styles.link}
                >
                  <ExternalLink size={14} />
                  Visit Website
                </a>
              )}
              {band.instagram_url && (
                <a
                  href={band.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={styles.link}
                >
                  <ExternalLink size={14} />
                  Instagram
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons - Pass, Match, and Connect (only for matched bands) */}
      <div className={styles.actions}>
        <button className={`${styles.actionBtn} ${styles.passBtn}`} onClick={handlePass} aria-label="Pass">
          <X size={28} />
        </button>
        <button className={`${styles.actionBtn} ${styles.matchBtn}`} onClick={handleMatch} aria-label="Match">
          <Heart size={28} />
        </button>
        {showConnect && (
          <button className={`${styles.actionBtn} ${styles.connectBtn}`} onClick={handleConnect} aria-label="Connect">
            <Check size={28} />
          </button>
        )}
      </div>
    </div>
  );
}
